import { useEffect, useState } from 'react';
import {
    Row,
    Col,
    Container,
    CardHeader,
    CardBody,
    CardFooter,
    Button,
    Badge,
    Table,
    Card
} from "reactstrap";
import Dropzone from "react-dropzone";
import Papa from "papaparse";
import { api, ui_helpers, date_helpers, filter_helpers } from "../helpers";
import {
    FilterSet,
    TextFilter,
    ResultHeader,
    Pager,
    AdminHeader,
    Loading,
} from "../components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Alert from "react-s-alert-v3";
import { Redirect } from 'react-router-dom';
import _ from "lodash";
import { default as useUserStore } from "../hooks/useUserStore";

export default function RTOProTaxZones(props) {
    const [zones, setZones] = useState([]);
    const [filters, setFilters] = useState([]);
    const [pagedList, setPagedList] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [redirect, setRedirect] = useState('');
    const [previewFile, setPreviewFile] = useState(null);
    const [previewRowCount, setPreviewRowCount] = useState(0);
    const [previewErrors, setPreviewErrors] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const currentUser = useUserStore(x => x.currentUser);

    useEffect(() => {
        refreshList();
    }, []);

    useEffect(() => {
        if (!pageNumber || (pagedList && pagedList.page === pageNumber)) return;
        refreshList(null, pageNumber);
    }, [pageNumber]);

    useEffect(() => {
        if (ui_helpers.isSystemAdministrator(currentUser)) return;
        setRedirect('/');
    }, []);

    function refreshList(newFilters, newPageNumber) {
        if (isLoading) return;
        setIsLoading(true);
        const resolvedFilters = newFilters || filters;
        const payload = {
            pageSize: 100,
            page: newPageNumber || (pagedList ? pagedList.pageNumber : 1),
            ...filter_helpers.filtersToObject(resolvedFilters)
        };
        api.post("reference/RTOProTaxZoneList", payload).then(response => {
            setPageNumber(response.data.data.pageNumber);
            setZones(response.data.data.dataSource);
            setPagedList({
                page: response.data.data.pageNumber,
                pageSize: response.data.data.pageSize,
                totalItems: response.data.data.totalItems
            });
            setFilters(resolvedFilters);
        }).catch(error =>
            Alert.error("There was an error loading the RTO Pro tax zone list.")
        ).finally(() => setIsLoading(false));
    }

    function filterChange(changed_filter) {
        const newFilters = filter_helpers.getFilters(filters, changed_filter);
        refreshList(newFilters, 1);
    }

    function clearFilters() {
        refreshList([], 1);
    }

    function onDrop(file) {
        if (!file) return;
        setPreviewFile(file);
        setPreviewErrors([]);
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                const errors = [];
                results.data.forEach((row, i) => {
                    const taxZone = row.taxzone || row.TaxZone || row.TAXZONE;
                    const taxRate = row.taxrate || row.TaxRate || row.TAXRATE;
                    if (!taxZone) {
                        errors.push(`Row ${i + 2}: missing taxzone`);
                    } else if (!taxRate || isNaN(parseFloat(taxRate))) {
                        errors.push(`Row ${i + 2}: invalid taxrate for zone '${taxZone}'`);
                    }
                });
                setPreviewRowCount(results.data.length);
                setPreviewErrors(errors);
            },
            error: () => Alert.error("There was an error reading that file.")
        });
    }

    function clearPreview() {
        setPreviewFile(null);
        setPreviewRowCount(0);
        setPreviewErrors([]);
    }

    function performUpload() {
        if (!previewFile || isUploading) return;
        setIsUploading(true);
        const payload = new FormData();
        payload.append('file', previewFile);
        api.post_form_data("reference/ImportRTOProTaxZones", payload).then(response => {
            if (response.data.success) {
                const result = response.data.data;
                const skipped = (result.skippedRowMessages || []);
                Alert.success(`Imported ${result.rowsImported} tax zone(s).` +
                    (skipped.length ? ` ${skipped.length} row(s) skipped.` : ''));
                skipped.forEach(m => Alert.warning(m));
                clearPreview();
                refreshList(null, 1);
                setShowImport(false);
            } else {
                Alert.error(response.data.message || "There was an error importing the file.");
            }
        }).catch(() => Alert.error("There was an error importing the file."))
        .finally(() => setIsUploading(false));
    }

    if (redirect) {
        return <Redirect to={redirect}/>;
    }

    return (<>
        <AdminHeader rtoProTaxZones/>
        <Row className='m-3 border-dark'>
            <FilterSet
                filters={filters}
                clearFilters={clearFilters}
                action={refreshList}
            >
                <Col sm={2}>
                    <TextFilter
                        filterName="taxZone"
                        displayName="Tax Zone"
                        descriptionPrefix="contains"
                        onChangeCallback={filterChange}
                    />
                </Col>
                <Col sm={2}>
                    <TextFilter
                        filterName="state"
                        displayName="State"
                        onChangeCallback={filterChange}
                    />
                </Col>
                <Col sm={2}>
                    <TextFilter
                        filterName="county"
                        displayName="County"
                        descriptionPrefix="contains"
                        onChangeCallback={filterChange}
                    />
                </Col>
                <Col sm={2}>
                    <TextFilter
                        filterName="city"
                        displayName="City"
                        descriptionPrefix="contains"
                        onChangeCallback={filterChange}
                    />
                </Col>
            </FilterSet>
        </Row>
        <Container fluid>
            <CardHeader>
                <Row className='d-flex align-items-center'>
                    <Col>
                        <ResultHeader heading='RTO Pro Tax Zones' totalResults={pagedList ? pagedList.totalItems : 0}/>
                    </Col>
                    <Col xs="auto">
                        <Button color="secondary" outline size="sm" onClick={() => setShowImport(!showImport)}>
                            <FontAwesomeIcon icon={showImport ? "chevron-circle-up" : "chevron-circle-down"}/>{' '}
                            {showImport ? "Hide Import" : "Show Import"}
                        </Button>
                    </Col>
                </Row>
            </CardHeader>
            {showImport ? (
                <Card className="mb-3">
                    <CardHeader className="d-flex align-items-center">
                        <FontAwesomeIcon icon="file-csv" size="lg" className="text-secondary me-2"/>
                        <div>
                            <strong>Import Tax Zones</strong>
                            <div className="text-muted" style={{fontSize: "12px"}}>
                                Required columns: taxzone, state, county, city, district1, district2, district3, taxrate
                            </div>
                            <div className="text-muted" style={{fontSize: "12px"}}>
                                Note: Existing records will be updated with imported data.
                            </div>
                        </div>
                    </CardHeader>
                    <CardBody>
                        <Row>
                            <Col sm={6}>
                                <Dropzone onDrop={fileList => onDrop(fileList[0])} accept=".csv" style={{width: "100%"}}>
                                    {({ getRootProps, getInputProps }) => (
                                        <div {...getRootProps()} className="text-center border rounded p-3">
                                            <input {...getInputProps()} />
                                            <div className="mb-1">Drop a tax zone CSV here or click to select</div>
                                            <FontAwesomeIcon icon={previewFile ? "check-square" : "file-upload"} size="2x"/>
                                        </div>
                                    )}
                                </Dropzone>
                            </Col>
                            <Col sm={6}>
                                {previewFile ? (
                                    <div>
                                        <p className="mb-2"><strong>{previewFile.name}</strong></p>
                                        <p className="mb-2">
                                            <Badge color="success" pill>{previewRowCount - previewErrors.length} ready</Badge>
                                            {previewErrors.length ? (
                                                <>{' '}<Badge color="warning" pill>{previewErrors.length} skipped</Badge></>
                                            ) : null}
                                        </p>
                                        {previewErrors.length ? (
                                            <div className="text-warning">
                                                <p className="mb-1">{previewErrors.length} row(s) look invalid and will be skipped:</p>
                                                <ul>
                                                    {previewErrors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
                                                </ul>
                                            </div>
                                        ) : null}
                                    </div>
                                ) : (
                                    <div className="text-muted d-flex align-items-center h-100">
                                        Select a file to preview it here.
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </CardBody>
                    <CardFooter className="text-end">
                        <Button className="btn-secondary" size="sm" disabled={!previewFile} onClick={clearPreview}>
                            <FontAwesomeIcon icon="times-circle"/> Cancel
                        </Button>{' '}
                        <Button
                            className="btn-success"
                            style={{color: "white"}}
                            size="sm"
                            disabled={!previewFile || isUploading}
                            onClick={performUpload}
                        >
                            <FontAwesomeIcon icon="save"/> Import
                        </Button>
                    </CardFooter>
                </Card>
            ) : null}
        </Container>
        <Row className='m-2'>
            <Pager pageNumber={pageNumber} pageSize={pagedList ? pagedList.pageSize : 0}
                   totalResults={pagedList ? pagedList.totalItems : 0}
                   onSetPageCallback={setPageNumber}/>
        </Row>
        <Container fluid>
            <Table size="sm" bordered striped>
                <thead>
                <tr>
                    <th>Tax Zone</th>
                    <th>State</th>
                    <th>County</th>
                    <th>City</th>
                    <th>District 1</th>
                    <th>District 2</th>
                    <th>District 3</th>
                    <th>Tax Rate</th>
                    <th>Last Imported</th>
                </tr>
                </thead>
                <tbody>
                {isLoading
                    ? <tr><td><Loading/></td></tr>
                    : _.map(zones, z => (
                        <tr key={`rtopro-taxzone-${z.id}`}>
                            <td>{z.taxZone}</td>
                            <td>{z.state}</td>
                            <td>{z.county}</td>
                            <td>{z.city}</td>
                            <td>{z.district1}</td>
                            <td>{z.district2}</td>
                            <td>{z.district3}</td>
                            <td>{z.taxRate}</td>
                            <td>{date_helpers.formatDateToShortDate(z.lastReplicatedAt)}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </Container>
    </>);
}
